import type { CSSProperties } from 'react';

import './landingRef.css';

type Accent = 'violet' | 'teal' | 'blue' | 'KYN' | 'rose' | 'green';

type ChipDef = {
  readonly label: string;
  readonly cssX: number;
  readonly cssY: number;
  readonly accent: Accent;
  readonly delay: number;
  readonly lineFrom: readonly [number, number];
  readonly lineTo: readonly [number, number];
};

const CHIPS: readonly ChipDef[] = [
  {
    label: 'Orientation openness',
    cssX: -220,
    cssY: -78,
    accent: 'violet',
    delay: 0,
    lineFrom: [154, 122],
    lineTo: [208, 140],
  },
  {
    label: 'Gender openness',
    cssX: -220,
    cssY: 0,
    accent: 'teal',
    delay: 0.5,
    lineFrom: [146, 200],
    lineTo: [200, 200],
  },
  {
    label: 'Chosen-family energy',
    cssX: -220,
    cssY: 78,
    accent: 'blue',
    delay: 1,
    lineFrom: [157, 278],
    lineTo: [208, 260],
  },
  {
    label: 'Friendship and dating',
    cssX: 220,
    cssY: -78,
    accent: 'KYN',
    delay: 0.3,
    lineFrom: [448, 122],
    lineTo: [392, 140],
  },
  {
    label: 'Nothing to explain',
    cssX: 220,
    cssY: 0,
    accent: 'rose',
    delay: 0.8,
    lineFrom: [442, 200],
    lineTo: [400, 200],
  },
  {
    label: 'Prompts about you',
    cssX: 220,
    cssY: 78,
    accent: 'green',
    delay: 1.2,
    lineFrom: [438, 278],
    lineTo: [392, 260],
  },
] as const;

function chipStyle(cssX: number, cssY: number, delay: number): CSSProperties {
  return {
    '--chip-x-num': cssX,
    '--chip-y-num': cssY,
    animationDelay: `${delay}s`,
  } as CSSProperties;
}

function makePath(from: readonly [number, number], to: readonly [number, number]): string {
  const [fx, fy] = from;
  const [tx, ty] = to;
  const goRight = tx > fx;
  const gap = Math.abs(tx - fx);
  const t = Math.min(gap * 0.55, 28);
  const cp1x = goRight ? fx + t : fx - t;
  const cp2x = goRight ? tx - t : tx + t;
  return `M ${fx} ${fy} C ${cp1x} ${fy} ${cp2x} ${ty} ${tx} ${ty}`;
}

/** Preference-field visual — glowing ring, center card, 6 satellite chips with gold S-curve connections. */
export function OpenMindedVisual() {
  return (
    <div className="open-minded-visual open-minded-visual--field" aria-hidden>
      <div className="open-minded-visual__canvas">
        <div className="open-minded-visual__fieldRing" />
        <div className="open-minded-visual__halo" />

        <svg
          className="open-minded-visual__connections"
          viewBox="0 0 600 400"
          preserveAspectRatio="xMidYMid meet">
          {CHIPS.map((chip) => (
            <path
              key={chip.label}
              d={makePath(chip.lineFrom, chip.lineTo)}
              className="open-minded-visual__connectionLine"
            />
          ))}
        </svg>

        <div className="open-minded-visual__core">
          <div className="open-minded-visual__coreRim" />
          <div className="open-minded-visual__coreInner">
            <div className="open-minded-visual__coreOrb" aria-hidden>
              <span className="open-minded-visual__coreOrbRing open-minded-visual__coreOrbRing--outer" />
              <span className="open-minded-visual__coreOrbRing open-minded-visual__coreOrbRing--mid" />
              <span className="open-minded-visual__coreOrbRing open-minded-visual__coreOrbRing--inner" />
            </div>
            <p className="open-minded-visual__coreTitle">Your Circle</p>
            <p className="open-minded-visual__coreMeta">Built with openness in mind</p>
            <div className="open-minded-visual__coreDivider" aria-hidden>
              <span className="open-minded-visual__coreDividerLine" />
              <span className="open-minded-visual__coreDividerGem" />
              <span className="open-minded-visual__coreDividerLine" />
            </div>
            <p className="open-minded-visual__coreLine">
              A clearer circle for more fluid connection.
            </p>
          </div>
        </div>

        {CHIPS.map((chip) => (
          <span
            key={chip.label}
            className="open-minded-visual__chip"
            style={chipStyle(chip.cssX, chip.cssY, chip.delay)}>
            <span
              className={`open-minded-visual__chipDot open-minded-visual__chipDot--${chip.accent}`}
            />
            <span className="open-minded-visual__chipLabel">{chip.label}</span>
          </span>
        ))}
      </div>
    </div>
  );
}
